import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import { CompareArrows, TrendingUp, TrendingDown, Remove, CheckCircle, Warning, BarChart, ExpandMore, ExpandLess, Replay, AutoAwesome, Psychology, OpenInNew, Delete, Refresh } from '@mui/icons-material';
import { PageHeader, Loading, ErrorMessage, ScoreChip, StatusBadge, ConfirmDialog, CandidateIdsInput } from './common';
import { apiService } from '../services/api';

const getMatchBarColor = (pct) => {
  if (pct >= 95) return { bar: '#2e7d32', text: '#2e7d32' };        // dark green
  if (pct >= 80) return { bar: '#66bb6a', text: '#388e3c' };        // light green
  if (pct >= 75) return { bar: '#fb8c00', text: '#e65100' };        // orange
  if (pct >= 60) return { bar: '#fdd835', text: '#f57f17' };        // yellow
  return { bar: '#e53935', text: '#c62828' };                        // red
};

const ExactMatchBar = ({ pct }) => {
  const { bar: barColor, text: textColor } = getMatchBarColor(pct);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 4,
          flexGrow: 1,
          backgroundColor: 'rgba(0,0,0,0.08)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: barColor,
          },
        }}
      />
      <Typography variant="caption" fontWeight={700} sx={{ minWidth: 45, color: textColor }}>
        {pct.toFixed(1)}%
      </Typography>
    </Box>
  );
};

const DiffChip = ({ gt, llm }) => {
  const diff = gt - llm;
  const isExact = Math.abs(diff) < 0.01;
  if (isExact) return <Chip icon={<CheckCircle fontSize="small" />} label="Exact" color="success" size="small" />;
  const isGtHigher = diff > 0.01;
  return <Chip
    icon={isGtHigher ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
    label={`${isGtHigher ? '+' : ''}${diff.toFixed(2)}`}
    color={isGtHigher ? 'warning' : 'info'}
    size="small"
    variant="outlined"
  />;
};

const AGGREGATION_MODES = [
  { value: 'first_run', label: 'First Run' },
  { value: 'last_run', label: 'Last Run' },
  { value: 'mean', label: 'Mean of All Runs' },
];

const ImprovementChip = ({ improvement }) => {
  if (improvement == null) return <Chip label="N/A" size="small" variant="outlined" />;
  const isImproved = improvement > 0.01;
  const isWorsened = improvement < -0.01;
  const color = isImproved ? 'success' : isWorsened ? 'error' : 'default';
  const label = isImproved ? `+${improvement.toFixed(2)}` : isWorsened ? improvement.toFixed(2) : 'Same';
  return <Chip label={label} color={color} size="small" variant={isImproved || isWorsened ? 'filled' : 'outlined'} />;
};

const EVAL_TYPES = [
  { value: 'all', label: 'All Evaluations' },
  { value: 'agentic', label: 'Agentic Only' },
  { value: 'standard', label: 'Standard Only' },
];

const Analytics = () => {
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [candidateIds, setCandidateIds] = useState('');
  const [tab, setTab] = useState(0);
  const [aggregationMode, setAggregationMode] = useState('last_run');
  const [evaluationType, setEvaluationType] = useState('standard');
  const [expandedAspects, setExpandedAspects] = useState(new Set());
  const [reevaluateOpen, setReevaluateOpen] = useState(false);
  const [reevaluating, setReevaluating] = useState(false);
  const [reevaluateAgentic, setReevaluateAgentic] = useState(false);
  const [reevaluateCandidateIds, setReevaluateCandidateIds] = useState('');

  const [rescoreOpen, setRescoreOpen] = useState(false);
  const [rescoreControlSum, setRescoreControlSum] = useState('');
  const [rescoreAgentic, setRescoreAgentic] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [rescoreResult, setRescoreResult] = useState(null);
  const [rescoreCandidateIds, setRescoreCandidateIds] = useState('');

  // Rescore Diff
  const [rescoreDiffControlSum, setRescoreDiffControlSum] = useState('');
  const [rescoreDiffEvalType, setRescoreDiffEvalType] = useState('all');
  const [rescoreDiffData, setRescoreDiffData] = useState(null);
  const [rescoreDiffLoading, setRescoreDiffLoading] = useState(false);
  const [rescoreDiffError, setRescoreDiffError] = useState(null);

  // Runs overview
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadRecentSubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const res = await apiService.submissions.list({ page_size: 20 });
      setRecentSubmissions(res.data.results || []);
    } catch (err) {
      console.error('Error loading submissions:', err);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  // Load recent submissions on mount
  React.useEffect(() => {
    loadRecentSubmissions();
  }, []);

  const handleDeleteSubmission = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.submissions.delete(deleteTarget);
      setDeleteOpen(false);
      setDeleteTarget(null);
      loadRecentSubmissions();
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert(err.response?.data?.error || 'Failed to delete submission');
    } finally {
      setDeleting(false);
    }
  };

  const toggleAspect = (controlSumId) => {
    const next = new Set(expandedAspects);
    if (next.has(controlSumId)) next.delete(controlSumId);
    else next.add(controlSumId);
    setExpandedAspects(next);
  };

  const detailedByAspect = useMemo(() => {
    if (!comparisonData?.detailed_comparisons) return {};
    const map = {};
    for (const item of comparisonData.detailed_comparisons) {
      const key = item.control_sum_id;
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => Math.abs(b.ground_truth_score - b.llm_score) - Math.abs(a.ground_truth_score - a.llm_score));
    }
    return map;
  }, [comparisonData]);

  const handleCompare = async (all = false) => {
    let ids = [];
    if (!all) {
      ids = candidateIds.split(/[\n,\s]+/).map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (ids.length < 1) {
        setError('Please enter at least one candidate ID or use Compare All');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      setComparisonData(null);
      const response = await apiService.comparisons.compareMultiple(ids, aggregationMode, evaluationType);
      setComparisonData(response.data);
    } catch (err) {
      setError('Failed to compare candidates: ' + (err.response?.data?.error || err.message));
      console.error('Error comparing candidates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReevaluateAll = async () => {
    setReevaluating(true);
    try {
      const payload = { agentic: reevaluateAgentic };
      if (reevaluateCandidateIds.trim()) {
        const ids = reevaluateCandidateIds.split(/[\n,\s]+/).map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        if (ids.length > 0) {
          payload.candidate_ids = ids;
        }
      }
      const response = await apiService.comparisons.reevaluateAll(payload);
      alert(`Re-evaluation queued! ${response.data.queued} of ${response.data.total} candidates queued.`);
      setReevaluateOpen(false);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setReevaluating(false);
    }
  };

  const handleRescoreAspect = async () => {
    const csId = parseInt(rescoreControlSum, 10);
    if (isNaN(csId)) {
      alert('Please select or enter a valid aspect (control sum ID).');
      return;
    }
    setRescoring(true);
    setRescoreResult(null);
    try {
      const payload = {
        control_sum_id: csId,
        agentic: rescoreAgentic,
      };
      if (rescoreCandidateIds.trim()) {
        const ids = rescoreCandidateIds.split(/[\n,\s]+/).map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        if (ids.length > 0) payload.candidate_ids = ids;
      }
      const response = await apiService.comparisons.rescoreAspect(payload);
      setRescoreResult(response.data);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setRescoring(false);
    }
  };

  const handleFetchRescoreDiff = async () => {
    const csId = parseInt(rescoreDiffControlSum, 10);
    if (isNaN(csId)) {
      setRescoreDiffError('Please select a valid aspect');
      return;
    }
    setRescoreDiffLoading(true);
    setRescoreDiffError(null);
    setRescoreDiffData(null);
    try {
      const response = await apiService.comparisons.getRescoreDiff(csId, rescoreDiffEvalType);
      setRescoreDiffData(response.data);
    } catch (err) {
      setRescoreDiffError(err.response?.data?.error || 'Failed to load rescore diff');
    } finally {
      setRescoreDiffLoading(false);
    }
  };

  const breakdownRows = useMemo(() => {
    if (!comparisonData?.overall_summary?.control_sum_breakdown) return [];
    const breakdown = comparisonData.overall_summary.control_sum_breakdown;
    return Object.values(breakdown).sort((a, b) =>
      (b.absolute_mean_difference || 0) - (a.absolute_mean_difference || 0)
    );
  }, [comparisonData]);

  const summary = comparisonData?.overall_summary;

  const calcTotalScore = (evaluations) => {
    if (!evaluations || evaluations.length === 0) return 0;
    return evaluations.reduce((total, ev) => {
      const isCP = ev.aspect_name && ev.aspect_name.toString().toUpperCase().includes('COUNTERPRODUCTIVE');
      return isCP ? total - (ev.score || 0) : total + (ev.score || 0);
    }, 0);
  };

  return (
    <Box>
      <PageHeader
        title="Analysis"
        subtitle="Multi-candidate comparison and evaluation run overview"
        actions={[
          { label: 'Rescore Aspect', icon: <Refresh />, onClick: () => { setRescoreResult(null); setRescoreCandidateIds(''); setRescoreOpen(true); }, color: 'warning' },
          { label: 'Re-evaluate All', icon: <Replay />, onClick: () => setReevaluateOpen(true), color: 'secondary' },
        ]}
      />

      {/* Runs Overview Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Evaluation Runs
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={loadRecentSubmissions}
              disabled={submissionsLoading}
              startIcon={<Replay fontSize="small" />}
            >
              Refresh
            </Button>
          </Box>

          {submissionsLoading && recentSubmissions.length === 0 ? (
            <Loading message="Loading runs..." size="small" />
          ) : recentSubmissions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No evaluation runs found.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell>Candidate</TableCell>
                    <TableCell align="center">Type</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Evals</TableCell>
                    <TableCell align="right">Total Score</TableCell>
                    <TableCell align="right">Ground Truth</TableCell>
                    <TableCell align="right">Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentSubmissions.map((sub) => {
                    const isAgentic = sub.evaluation_type === 'agentic';
                    const total = calcTotalScore(sub.evaluations || []);
                    return (
                      <TableRow key={sub.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {sub.candidate_id ? `Candidate ${sub.candidate_id}` : `Submission ${sub.id.slice(0, 8)}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={isAgentic ? <AutoAwesome fontSize="small" /> : <Psychology fontSize="small" />}
                            label={isAgentic ? 'Agentic' : 'Standard'}
                            color={isAgentic ? 'secondary' : 'default'}
                            size="small"
                            variant={isAgentic ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <StatusBadge status={sub.status} />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{sub.evaluations_count || 0}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <ScoreChip score={total} maxScore={80} />
                        </TableCell>
                        <TableCell align="right">
                          {sub.ground_truth_total != null ? (
                            <ScoreChip score={sub.ground_truth_total} maxScore={80} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            {sub.candidate_id && (
                              <Tooltip title="View all runs for this candidate">
                                <IconButton
                                  size="small"
                                  component={Link}
                                  to={`/candidate-detail/${sub.candidate_id}`}
                                  color="primary"
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View evaluation details">
                              <IconButton
                                size="small"
                                component={Link}
                                to={`/candidate/${sub.id}`}
                                color="primary"
                              >
                                <BarChart fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete submission">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeleteTarget(sub.id);
                                  setDeleteOpen(true);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Comparison Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Score Comparison
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Compare ground truth and LLM evaluation scores across multiple candidates.
          </Typography>

          {/* Mode selectors */}
          <Box sx={{ mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Aggregation:
              </Typography>
              <ToggleButtonGroup
                value={aggregationMode}
                exclusive
                onChange={(_, v) => v && setAggregationMode(v)}
                size="small"
              >
                {AGGREGATION_MODES.map((m) => (
                  <ToggleButton key={m.value} value={m.value}>
                    {m.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Evaluation type:
              </Typography>
              <ToggleButtonGroup
                value={evaluationType}
                exclusive
                onChange={(_, v) => v && setEvaluationType(v)}
                size="small"
              >
                {EVAL_TYPES.map((t) => (
                  <ToggleButton key={t.value} value={t.value}>
                    {t.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <CandidateIdsInput
              value={candidateIds}
              onChange={setCandidateIds}
              sx={{ minWidth: 300 }}
            />
            <Button
              variant="contained"
              onClick={() => handleCompare(false)}
              disabled={loading}
              startIcon={<CompareArrows />}
              size="small"
            >
              Compare Selected
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleCompare(true)}
              disabled={loading}
              startIcon={<BarChart />}
              size="small"
            >
              Compare All
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && <ErrorMessage message={error} onRetry={() => setError(null)} />}
      {loading && <Loading message="Comparing candidates..." />}

      {!loading && comparisonData && summary && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Mode label */}
          <Typography variant="body2" color="text.secondary">
            Showing results for: <strong>{AGGREGATION_MODES.find(m => m.value === aggregationMode)?.label}</strong> — <strong>{EVAL_TYPES.find(t => t.value === evaluationType)?.label}</strong>
          </Typography>

          {/* Overall Summary Cards */}
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Total Candidates</Typography>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {summary.total_candidates_compared || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Mean Abs Diff</Typography>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {summary.absolute_mean_difference?.toFixed(2) || '0.00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Avg Matched Aspects</Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {summary.average_matched_aspects?.toFixed(1) || '0.0'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Perfect Matches</Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {summary.candidates_with_perfect_matches || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab label={`Aspect Breakdown (${breakdownRows.length})`} />
            <Tab label="Exact Match Distribution" />
            <Tab label={`Detailed Comparisons (${comparisonData.detailed_comparisons?.length || 0})`} />
          </Tabs>

          {tab === 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Aspect-Level Breakdown
                </Typography>
                {breakdownRows.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell padding="checkbox" />
                          <TableCell>Aspect</TableCell>
                          <TableCell align="center">Control Sum</TableCell>
                          <TableCell align="right">Mean GT</TableCell>
                          <TableCell align="right">Mean LLM</TableCell>
                          <TableCell align="right">Abs Mean Diff</TableCell>
                          <TableCell align="center">Exact Match</TableCell>
                          <TableCell align="center">&plusmn;0.5 from GT%</TableCell>
                          <TableCell align="center">&plusmn;1.0 from GT%</TableCell>
                          <TableCell align="center">GT &gt; LLM</TableCell>
                          <TableCell align="center">LLM &gt; GT</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {breakdownRows.map((row) => (
                          <React.Fragment key={row.control_sum_id}>
                            <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleAspect(row.control_sum_id)}>
                              <TableCell padding="checkbox">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleAspect(row.control_sum_id); }}>
                                  {expandedAspects.has(row.control_sum_id) ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {row.leadership_capability_name}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">{row.control_sum_id}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700} color="success.main">
                                  {row.mean_ground_truth_score?.toFixed(2) || '0.00'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700} color="primary.main">
                                  {row.mean_llm_score?.toFixed(2) || '0.00'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700}>{row.absolute_mean_difference?.toFixed(2) || '0.00'}</Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 140 }}>
                                <ExactMatchBar pct={row.candidates_with_exact_match_percentage || 0} />
                                <Typography variant="caption" color="text.secondary">
                                  {row.candidates_with_exact_match} / {row.candidates_evaluated}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 140 }}>
                                <ExactMatchBar pct={row.candidates_within_0_5_percentage || 0} />
                                <Typography variant="caption" color="text.secondary">
                                  {row.candidates_within_0_5 || 0} / {row.candidates_evaluated}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 140 }}>
                                <ExactMatchBar pct={row.candidates_within_1_0_percentage || 0} />
                                <Typography variant="caption" color="text.secondary">
                                  {row.candidates_within_1_0 || 0} / {row.candidates_evaluated}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={row.candidates_ground_truth_higher || 0} color="warning" size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={row.candidates_llm_higher || 0} color="info" size="small" variant="outlined" />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={12} sx={{ p: 0, borderBottom: 'none' }}>
                                <Collapse in={expandedAspects.has(row.control_sum_id)} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                      Individual Evaluations — {row.leadership_capability_name}
                                    </Typography>
                                    {detailedByAspect[row.control_sum_id]?.length > 0 ? (
                                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow sx={{ backgroundColor: 'background.paper' }}>
                                              <TableCell>Candidate</TableCell>
                                              <TableCell align="right">GT Score</TableCell>
                                              <TableCell align="right">LLM Score</TableCell>
                                              <TableCell align="center">Diff</TableCell>
                                              <TableCell>Explanation</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {detailedByAspect[row.control_sum_id].map((det, idx) => (
                                              <TableRow key={idx} hover>
                                                <TableCell>
                                                  <Link to={`/candidate-detail/${det.candidate_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ '&:hover': { color: 'primary.main' } }}>
                                                      {det.candidate_id}
                                                    </Typography>
                                                  </Link>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography fontWeight={700} color="success.main">{det.ground_truth_score?.toFixed(2)}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography fontWeight={700} color="primary.main">{det.llm_score?.toFixed(2)}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                  <DiffChip gt={det.ground_truth_score} llm={det.llm_score} />
                                                </TableCell>
                                                <TableCell>
                                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 600 }}>
                                                    {det.explanation || 'No explanation available'}
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">No detailed data available.</Typography>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">No aspect breakdown data available.</Typography>
                )}
              </CardContent>
            </Card>
          )}

          {tab === 1 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Exact Match Distribution by Aspect
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {breakdownRows.length > 0 ? breakdownRows.map((row) => (
                    <Box key={row.control_sum_id} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {row.leadership_capability_name} <Typography component="span" color="text.secondary">(ID: {row.control_sum_id})</Typography>
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {row.candidates_with_exact_match_percentage?.toFixed(1) || '0.0'}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(row.candidates_with_exact_match_percentage || 0, 100)}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          mb: 1,
                          backgroundColor: 'rgba(0,0,0,0.08)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getMatchBarColor(row.candidates_with_exact_match_percentage || 0).bar,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          Exact: {row.candidates_with_exact_match} | GT &gt; LLM: {row.candidates_ground_truth_higher} | LLM &gt; GT: {row.candidates_llm_higher}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Evaluated: {row.candidates_evaluated} candidates
                        </Typography>
                      </Box>
                    </Box>
                  )) : (
                    <Typography color="text.secondary">No data available.</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {tab === 2 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Detailed Comparisons
                </Typography>
                {comparisonData.detailed_comparisons?.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell>Candidate</TableCell>
                          <TableCell>Aspect</TableCell>
                          <TableCell align="center">Control Sum</TableCell>
                          <TableCell align="right">GT Score</TableCell>
                          <TableCell align="right">LLM Score</TableCell>
                          <TableCell align="center">Diff</TableCell>
                          <TableCell>Explanation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparisonData.detailed_comparisons.map((det, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Link to={`/candidate-detail/${det.candidate_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <Typography variant="body2" fontWeight={600} sx={{ '&:hover': { color: 'primary.main' } }}>
                                  {det.candidate_id}
                                </Typography>
                              </Link>
                            </TableCell>
                            <TableCell>{det.aspect_name || 'Unknown'}</TableCell>
                            <TableCell align="center">{det.control_sum_id}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={700} color="success.main">{det.ground_truth_score?.toFixed(2)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={700} color="primary.main">{det.llm_score?.toFixed(2)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <DiffChip gt={det.ground_truth_score} llm={det.llm_score} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 600 }}>
                                {det.explanation || 'No explanation available'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">No detailed comparison data available.</Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Rescore Results Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Rescore Results
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Compare the two most recent evaluations per candidate to see how scores changed after a rescore.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
            {breakdownRows.length > 0 ? (
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel>Aspect</InputLabel>
                <Select
                  value={rescoreDiffControlSum}
                  label="Aspect"
                  onChange={(e) => setRescoreDiffControlSum(e.target.value)}
                >
                  <MenuItem value=""><em>Select an aspect...</em></MenuItem>
                  {breakdownRows.map((row) => (
                    <MenuItem key={row.control_sum_id} value={row.control_sum_id}>
                      {row.leadership_capability_name} (CS {row.control_sum_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Control Sum ID"
                placeholder="e.g. 15"
                value={rescoreDiffControlSum}
                onChange={(e) => setRescoreDiffControlSum(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                helperText="Run a comparison first to see aspect options"
              />
            )}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Evaluation type:
              </Typography>
              <ToggleButtonGroup
                value={rescoreDiffEvalType}
                exclusive
                onChange={(_, v) => v && setRescoreDiffEvalType(v)}
                size="small"
              >
                {EVAL_TYPES.map((t) => (
                  <ToggleButton key={t.value} value={t.value}>
                    {t.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Button
              variant="contained"
              onClick={handleFetchRescoreDiff}
              disabled={rescoreDiffLoading || !rescoreDiffControlSum}
              startIcon={<BarChart />}
              size="small"
            >
              Analyze
            </Button>
          </Box>

          {rescoreDiffError && <ErrorMessage message={rescoreDiffError} onRetry={() => setRescoreDiffError(null)} />}
          {rescoreDiffLoading && <Loading message="Loading rescore diff..." />}

          {rescoreDiffData && (
            <Box sx={{ mt: 2 }}>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Improved</Typography>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {rescoreDiffData.summary.candidates_improved || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Worsened</Typography>
                      <Typography variant="h4" fontWeight={700} color="error.main">
                        {rescoreDiffData.summary.candidates_worsened || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Unchanged</Typography>
                      <Typography variant="h4" fontWeight={700}>
                        {rescoreDiffData.summary.candidates_unchanged || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Mean Improvement</Typography>
                      <Typography variant="h4" fontWeight={700} color={(rescoreDiffData.summary.mean_improvement || 0) > 0 ? 'success.main' : (rescoreDiffData.summary.mean_improvement || 0) < 0 ? 'error.main' : 'text.primary'}>
                        {(rescoreDiffData.summary.mean_improvement || 0).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {rescoreDiffData.summary.candidates_with_two_runs} candidates with 2+ runs compared,
                {' '}{rescoreDiffData.summary.candidates_with_single_run} with only 1 run (excluded from diff).
                {rescoreDiffData.summary.is_counterproductive && ' (Counterproductive aspect — scores negated)'}
              </Typography>

              {rescoreDiffData.summary.candidates_with_two_runs === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No candidates have been rescored for this aspect yet. Rescore the aspect first, then come back to see the before/after comparison.
                </Alert>
              )}

              {/* Per-candidate diff table */}
              {rescoreDiffData.candidate_diffs?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>Candidate</TableCell>
                        <TableCell align="right">GT Score</TableCell>
                        <TableCell align="right">Old Score</TableCell>
                        <TableCell align="right">New Score</TableCell>
                        <TableCell align="center">Delta</TableCell>
                        <TableCell align="center">Improvement</TableCell>
                        <TableCell>Old Run</TableCell>
                        <TableCell>New Run</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rescoreDiffData.candidate_diffs.map((row) => (
                        <TableRow key={row.candidate_id} hover>
                          <TableCell>
                            <Link to={`/candidate-detail/${row.candidate_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                              <Typography variant="body2" fontWeight={600} sx={{ '&:hover': { color: 'primary.main' } }}>
                                {row.candidate_id}
                              </Typography>
                            </Link>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={700} color="success.main">{row.ground_truth_score?.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>{row.old_score?.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={700} color="primary.main">{row.new_score?.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <DiffChip gt={row.new_score} llm={row.old_score} />
                          </TableCell>
                          <TableCell align="center">
                            <ImprovementChip improvement={row.improvement} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {row.old_evaluation_type} &middot; {row.old_created_at ? new Date(row.old_created_at).toLocaleDateString() : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {row.new_evaluation_type} &middot; {row.new_created_at ? new Date(row.new_created_at).toLocaleDateString() : ''}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No candidates with 2+ evaluation runs found for this aspect.</Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Re-evaluate All Dialog */}
      <Dialog open={reevaluateOpen} onClose={() => setReevaluateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Re-evaluate All Candidates</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create a new evaluation run for every candidate that has ground truth scores.
            Leave candidate IDs empty to re-evaluate all, or specify IDs to re-evaluate only selected candidates.
          </Typography>
          <CandidateIdsInput
            value={reevaluateCandidateIds}
            onChange={setReevaluateCandidateIds}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={reevaluateAgentic} onChange={(e) => setReevaluateAgentic(e.target.checked)} />}
            label="Use agentic evaluation mode"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReevaluateOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleReevaluateAll}
            variant="contained"
            color="primary"
            disabled={reevaluating}
            startIcon={<Replay />}
          >
            {reevaluating ? 'Starting...' : 'Start Re-evaluation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Submission"
        message="This will permanently delete this submission and all its evaluation results. This action cannot be undone."
        onConfirm={handleDeleteSubmission}
        onCancel={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        confirmDisabled={deleting}
        confirmColor="error"
      />

      {/* Rescore Aspect Dialog */}
      <Dialog open={rescoreOpen} onClose={() => setRescoreOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rescore Aspect</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Re-evaluate a single aspect. Leave candidate IDs empty to rescore all candidates with ground truth scores, or specify IDs to rescore only selected candidates.
          </Typography>
          {breakdownRows.length > 0 ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Aspect</InputLabel>
              <Select
                value={rescoreControlSum}
                label="Aspect"
                onChange={(e) => setRescoreControlSum(e.target.value)}
              >
                <MenuItem value=""><em>Select an aspect...</em></MenuItem>
                {breakdownRows.map((row) => (
                  <MenuItem key={row.control_sum_id} value={row.control_sum_id}>
                    {row.leadership_capability_name} (CS {row.control_sum_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Control Sum ID"
              placeholder="e.g. 15"
              value={rescoreControlSum}
              onChange={(e) => setRescoreControlSum(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              helperText="Run a comparison first to see aspect options, or enter the control sum ID manually"
            />
          )}
          <CandidateIdsInput
            value={rescoreCandidateIds}
            onChange={setRescoreCandidateIds}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={rescoreAgentic} onChange={(e) => setRescoreAgentic(e.target.checked)} />}
            label="Use agentic evaluation mode"
          />
          {rescoreResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.lighter', borderRadius: 1, border: 1, borderColor: 'success.light' }}>
              <Typography variant="subtitle2" fontWeight={700} color="success.dark" gutterBottom>
                Rescore Queued
              </Typography>
              <Typography variant="body2">
                <strong>{rescoreResult.aspect_name}</strong> (CS {rescoreResult.control_sum_id})
              </Typography>
              <Typography variant="body2">
                {rescoreResult.queued} of {rescoreResult.total_candidates} candidates queued ({rescoreResult.evaluation_type} mode)
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescoreOpen(false)} color="inherit">
            Close
          </Button>
          <Button
            onClick={handleRescoreAspect}
            variant="contained"
            color="warning"
            disabled={rescoring || !rescoreControlSum}
            startIcon={<Refresh />}
          >
            {rescoring ? 'Starting...' : 'Start Rescore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Analytics;